import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import ReactGA from 'react-ga4';

import Button from './Button';
import { Dropdown, Icon } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import * as portfolioApi from '~/api/portfolios';
import * as projectApi from '~/api/projects';
import { usePageType, usePortfolioContext, useProjectContext } from '~/hooks';
import axios from 'axios';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { logUserActivity, UserActivityTypes } from '~/api/user';
import styled from 'styled-components';
import Modal from '~/components/Modal';
import { useModalContext } from '~/hooks/providers/ModalProvider';

const DropdownWrapper = styled.div<{ isPortfolio?: boolean }>`
  --color-primary: ${props => props.isPortfolio ? 'var(--color-blue)' : 'var(--color-orange)'};
`;

function DeleteSummaryDocumentConfirmationModal() {
  const { closeModal, modalProps } = useModalContext();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await modalProps.onConfirm();
      closeModal();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal allowClose={!isDeleting} closeModal={isDeleting ? undefined : closeModal}>
      <Modal.Title>
        Delete Deal summary document?
      </Modal.Title>
      <Modal.Content>
        <p>
          This will remove the manually uploaded Deal summary document.
        </p>
      </Modal.Content>
      <Modal.Footer>
        <Button
          primary
          outlined
          onClick={() => closeModal()}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          primary
          onClick={handleConfirmDelete}
          loading={isDeleting}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function useSummaryDocument() {
  const { portfolio } = usePortfolioContext();
  const { project } = useProjectContext();
  const { openModal } = useModalContext();
  const pageType = usePageType();
  const [hasUploadedDoc, sethasUploadedDoc] = useState(false);

  //  Summary docs are always stored in the parent and accessible to all investors
  const portfolioId = portfolio?.parentId ?? portfolio?.id;
  const projectId = project?.parentId ?? project?.id;

  const { mutateAsync: fetchExistingDocument, status: fetchStatus } = useMutation(async () => {
    try {
      return portfolio ?
        await portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId)
        : await projectApi.getProjectSummaryDownloadUrl(projectId);
    } catch (err) {
      // Need this for when there is no document found
    }
  });

  useEffect(() => {
    fetchExistingDocument()
      .then(data => sethasUploadedDoc(!!data?.presignedUrl));
  }, [fetchExistingDocument]);

  const { mutateAsync: downloadFile, status: downloadStatus } = useMutation(async () => {
    try {
      ReactGA.event({
        category: 'Downloads',
        action: 'Download Conductor summary document',
        label: (project ? project?.name : portfolio?.name) || 'Unknown project'
      });
      logUserActivity({
        activityType: UserActivityTypes.SUMMARY_DOCUMENT_DOWNLOAD,
        ...(portfolio ? { portfolioId } : { projectId }),
      });
      // Navigate to the presigned S3 url to trigger the download
      fetchExistingDocument()
        .then(data => window.location = data?.presignedUrl);
    } catch (err) {
      console.error(err);
      toast.error('Error occurred downloading the document. Please refresh the page and try again.');
    }
  });

  const { mutateAsync: removeFile, status: removeFileStatus } = useMutation(async () => {
    try {
      portfolio ?
        await portfolioApi.removePortfolioSummaryDocument(portfolioId)
        : await projectApi.removeProjectSummaryDocument(projectId);

      sethasUploadedDoc(false);
      toast.success('Document removed');
    } catch (err) {
      console.error(err);
      toast.error('Error occurred removing the document. Please refresh the page and try again.');
    }
  });

  const generateFileName = (file: File) => {
    // Prefix the file with the first 4 alphanumeric, non-whitespace characters of the project or portfolio name
    const firstFourChars = portfolio ? portfolio.name.match(/[A-Za-z0-9]/g) ?? [] : project.name.match(/[A-Za-z0-9]/g) ?? [];
    return `${firstFourChars.slice(0, 4).join('').toUpperCase()}_${pageType}_summary.${file.name.split('.').pop()}`; // Use the original file extension
  };

  const { mutateAsync: uploadFile, status: uploadStatus } = useMutation(async (file: File) => {
    try {
      const result = portfolio ?
        await portfolioApi.uploadPortfolioSummaryDocument(portfolioId, generateFileName(file))
        : await projectApi.uploadProjectSummaryDocument(projectId, generateFileName(file));

      if (result?.presignedUrl) {
        await axios.put(result.presignedUrl, file);
        sethasUploadedDoc(true);
        toast.success('Document uploaded');
      } else {
        throw new Error('No presigned url returned when uploading summary document');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error occurred uploading the document. Please refresh the page and try again.');
    }
  });

  const { mutateAsync: replaceFile, status: replaceFileStatus } = useMutation(async (file: File) => {
    try {
      const result = portfolio ?
        await portfolioApi.updatePortfolioSummaryDocument(portfolioId, generateFileName(file))
        : await projectApi.updateProjectSummaryDocument(projectId, generateFileName(file));

      if (result?.presignedUrl) {
        await axios.put(result.presignedUrl, file);
        toast.success('Replacement document uploaded');
      } else {
        throw new Error('No presigned url returned when uploading summary document');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error occurred replacing the document. Please refresh the page and try again.');
    }
  });

  const dropzone = useDropzone({
    maxFiles: 1,
    async onDrop(acceptedFiles) {
      if (acceptedFiles?.length !== 1) {
        toast.error('Only one file can be uploaded for a row.');
      } else {
        hasUploadedDoc ?
          await replaceFile(acceptedFiles[0])
          : await uploadFile(acceptedFiles[0]);
      }
    }
  });

  const handleDeleteSummaryDocument = () => {
    openModal(DeleteSummaryDocumentConfirmationModal, {
      onConfirm: removeFile
    });
  };

  return {
    isPortfolio: !!portfolio,
    hasUploadedDoc,
    fetchStatus,
    downloadStatus,
    uploadStatus,
    removeFileStatus,
    replaceFileStatus,
    downloadFile,
    handleDeleteSummaryDocument,
    openFilePicker: dropzone.open,
    getRootProps: dropzone.getRootProps,
    getInputProps: dropzone.getInputProps,
  };
}

export default function SummaryDocumentButton() {
  const { permissions } = usePermissions();
  const {
    isPortfolio,
    hasUploadedDoc,
    fetchStatus,
    downloadStatus,
    uploadStatus,
    removeFileStatus,
    replaceFileStatus,
    downloadFile,
    handleDeleteSummaryDocument,
    openFilePicker,
    getRootProps,
  } = useSummaryDocument();

  if (permissions.isAdmin) {
    return (
      <div>
        {hasUploadedDoc ?
          <DropdownWrapper isPortfolio={isPortfolio}>
            <Dropdown
              icon={null}
              direction='left'
              loading={[fetchStatus, downloadStatus, uploadStatus, removeFileStatus, replaceFileStatus].includes('loading')}
              trigger={
                <Button secondary outlined size='small'>
                  <Icon name='file alternate outline' />
                  Deal summary
                </Button>
              }
            >
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => downloadFile()}>
                  Download
                </Dropdown.Item>
                <Dropdown.Item onClick={() => openFilePicker()}>
                  Replace
                </Dropdown.Item>
                <Dropdown.Item onClick={handleDeleteSummaryDocument}>
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </DropdownWrapper>
          :
          <Button
            secondary
            outlined
            loading={uploadStatus === 'loading'}
            size='small'
            {...(uploadStatus === 'loading' ? {} : getRootProps())}
          >
            <Icon name='cloud upload' />
            Upload Deal summary
          </Button>
        }
      </div>
    );
  } else if (permissions.isInvestor && hasUploadedDoc) {
    return (
      <div>
        <Button
          secondary
          outlined
          loading={downloadStatus === 'loading'}
          {...(downloadStatus === 'loading' ? {} : getRootProps())}
          size='small'
          onClick={() => downloadFile()}
        >
          <Icon name='file alternate outline' />
          Deal summary
        </Button>
      </div>
    );
  } else {
    return null;
  }
}
